import type { CreativeItem } from './creative-api';
import type { CreativeJob, VideoAspectRatio } from '@/store/slices/creativeJobs';
import { ALL_ASPECTS } from './aspect-ratios';

/**
 * Pure helpers for the page-local video composer (see `components/VideoComposer`). Kept out of the
 * component so the branchy bits (aspect guard, duration clamp, key-art lineage, the seeded prompt,
 * reload-survival job lookup) are unit-testable without rendering. Imports stay light on purpose: the
 * only value import is ALL_ASPECTS (pure data, no store/axios chain), never the store or a component.
 */

/** Placement recorded on a video job. The backend treats placement as informational for video and
 *  keys the "no banner-family version" invariant on an empty root banner, so this is just a label. */
export const VIDEO_PLACEMENT = 'video';

/** The clip aspect ratios the backend accepts. Re-exported from ALL_ASPECTS (ASPECT_META's keys) so the
 *  offer set has a single source and can't drift from the picker. */
export const VIDEO_ASPECTS: readonly VideoAspectRatio[] = ALL_ASPECTS;

/** Clip length bounds (seconds), matching the backend's 3..10 range and its default of 6. */
export const VIDEO_DURATION_MIN = 3;
export const VIDEO_DURATION_MAX = 10;
export const VIDEO_DURATION_DEFAULT = 6;

/** The selectable durations, 3..10 seconds. */
export const VIDEO_DURATIONS: number[] = Array.from(
  { length: VIDEO_DURATION_MAX - VIDEO_DURATION_MIN + 1 },
  (_unused, i) => VIDEO_DURATION_MIN + i,
);

/** Narrowing guard: is a raw string one of the supported aspect ratios? */
export const isVideoAspect = (value: string): value is VideoAspectRatio =>
  (VIDEO_ASPECTS as readonly string[]).includes(value);

/**
 * Clamp a requested duration into the accepted range. Matches the backend's `videoParamsFromRequest`:
 * a non-finite / zero / negative value is "unset" and falls back to the default (6); anything else is
 * rounded and clamped into 3..10.
 */
export const clampVideoDuration = (seconds: number): number => {
  if (!Number.isFinite(seconds) || seconds <= 0) return VIDEO_DURATION_DEFAULT;
  return Math.min(VIDEO_DURATION_MAX, Math.max(VIDEO_DURATION_MIN, Math.round(seconds)));
};

/** The lineage id a video run branches from: the promotion's published version, else its banner id. */
export const keyArtVersionId = (item: CreativeItem): string | undefined =>
  item.published_version_id || item.banner_id;

/** A minimal translator: the real `t` from `useTranslation`, or an identity `t` in tests. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The starting prompt for a promotion's video, seeded from its on-site copy. Three templates by data
 * presence (headline + subtitle / headline only / neither) so the owner starts from something real
 * and edits from there. `t` is injected so it is unit-testable with an identity translator.
 */
export const seedVideoPrompt = (item: CreativeItem, t: Translate): string => {
  const headline = item.headline?.trim();
  const subtitle = item.subtitle?.trim();
  if (headline && subtitle) {
    return t('Animate the "{{headline}}" promotion ({{subtitle}}) into a short, premium promotional video that keeps its look and branding.', {
      headline,
      subtitle,
    });
  }
  if (headline) {
    return t('Animate the "{{headline}}" promotion into a short, premium promotional video that keeps its look and branding.', {
      headline,
    });
  }
  return t('Animate this promotion into a short, premium promotional video that keeps its look and branding.');
};

/**
 * The newest job of a given kind for a tenant, for reload survival: on remount a composer shows the
 * last generation of its kind (running or done) without needing the in-session job id. Ignores other
 * kinds and other tenants. Pure, memoise at the call site.
 */
export const newestJobOfKind = (
  jobs: CreativeJob[],
  tenantId: number | string | undefined,
  kind: string,
): CreativeJob | undefined => {
  if (tenantId === undefined) return undefined;
  const tenant = String(tenantId);
  let newest: CreativeJob | undefined;
  for (const job of jobs) {
    if (job.kind !== kind) continue;
    if (String(job.tenant_id) !== tenant) continue;
    if (!newest || (job.created_at ?? '') > (newest.created_at ?? '')) newest = job;
  }
  return newest;
};

/**
 * The newest video-kind job for a tenant (reload survival for the video composer). A thin wrapper of
 * {@link newestJobOfKind} so VideoComposer stays untouched.
 */
export const newestVideoJob = (
  jobs: CreativeJob[],
  tenantId: number | string | undefined,
): CreativeJob | undefined => newestJobOfKind(jobs, tenantId, 'video');
